import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@material-ui/core';
import { Form } from '@unform/web';
import { assign, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select, Autocomplete, Hidden } from '~/components/Form';
import { t } from '~/helpers';
import { validationErrors, validation } from '~/helpers/yupValidations';
import { storeData, updateData } from '~/store/modules/preset/actions';

import { Revenues, Preset } from '../components';
import * as S from './styles';

export default function RequestForm({ reference, create, data }) {
  const dispatch = useDispatch();

  const userConfig = useSelector((state) => state.user.configs);

  const [components, setComponents] = useState([]);

  const [revenues, setRevenues] = useState([]);

  const { loading, find, params } = useSelector((state) => state.preset);

  const optionTCD = useMemo(
    () => [
      { value: '3', label: t('s.cadastro.tipo_preset_tcd') },
      { value: '6', label: t('s.cadastro.tipo_preset_danload_6000_tcd') },
      {
        value: '9',
        label: t('s.cadastro.tipo_preset_danload_8000_roclink_tcd'),
      },
      {
        value: '11',
        label: t('s.cadastro.tipo_preset_microload'),
      },
    ],
    []
  );

  const options = useMemo(
    () => [
      { value: 'L', label: t('s.geral.opcoes.liberado') },
      { value: 'B', label: t('s.geral.opcoes.bloqueado') },
      { value: 'C', label: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const optionTB = useMemo(
    () => [
      { label: 'Top', value: 'T' },
      { label: 'Bottom', value: 'B' },
    ],
    []
  );

  const tipoConexao = useMemo(
    () => [
      { value: 'S', label: t('s.cadastro.preset.conexaoclientepreset') },
      {
        value: 'N',
        label: t('s.cadastro.preset.conexaoclientepresetservidor'),
      },
    ],
    []
  );

  const initialData = useMemo(
    () =>
      create
        ? assign({ tipocarregamentopreset: 'T' }, data)
        : assign(find, data),
    [create, data]
  );

  const handleChangeComponents = useCallback((values = []) => {
    setComponents(values);
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const schema = Yup.object().shape({
          idpreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          nomepreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30)),
          idposto: Yup.number(t('s.geral.validacao.informeumnumero')).required(
            t('s.geral.validacao.campoobrigatorio')
          ),
          enderecopreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20)),
          conexaoclientepreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          tipopreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          statuspreset: Yup.string()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
          ordempreset: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .integer(t('s.geral.validacao.deveserinteiro')),
          portatcppreset: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .typeError(t('s.geral.validacao.campoobrigatorio')),
        });

        await validation(schema, values);

        const errors = [];

        if (isEmpty(components))
          errors.push(t('labelsistema.erro_componente_obrigatorio'));

        if (isEmpty(revenues))
          errors.push(t('s.cadastro.preset.erro_receita_obrigatoria'));

        const MapComp = components.map((comp) =>
          comp.medidorpresetproduto.toString()
        );

        const MapRev = revenues.map((rev) => rev.idpresetreceita.toString());

        MapComp.filter(
          (comp, index) => MapComp.indexOf(comp) !== index
        ).forEach((comp) => {
          errors.push(
            t('s.cadastro.preset.erro_medidor_em_uso').replace('%s', comp)
          );
        });

        MapRev.filter((rev, index) => MapRev.indexOf(rev) !== index).forEach(
          (rev) => {
            errors.push(
              t('s.cadastro.preset.erro_receita_repetida').replace('%s', rev)
            );
          }
        );

        if (!isEmpty(errors)) throw errors;

        const formData = {
          ...initialData,
          ...values,
          produtos: components.map((comp) => ({
            idpreset: values.idpreset,
            idproduto: comp.idproduto,
            medidorpresetproduto: Number(comp.medidorpresetproduto),
            nomeproduto: comp.nomeproduto,
            tagpresetproduto: comp.tagpresetproduto,
          })),
          receitas: revenues.map((rev) => {
            const revData = { ...rev };

            delete revData.id;

            return {
              ...revData,
              idpreset: values.idpreset,
              idpresetreceita: Number(rev.idpresetreceita),
            };
          }),
        };

        if (create) dispatch(storeData(formData, params));
        else dispatch(updateData(formData, params));
      } catch (error) {
        console.log(error);
        validationErrors(error, reference);
      }
    },
    [reference, initialData, create, dispatch, params, components, revenues]
  );

  if (loading)
    return (
      <S.WrapperLoading>
        <Spinner loading />
      </S.WrapperLoading>
    );

  return (
    <Form ref={reference} initialData={initialData} onSubmit={handleSubmit}>
      <Input
        name="idpreset"
        label={t('s.cadastro.preset.idpreset')}
        disabled={!create}
      />
      <Input name="nomepreset" label={t('s.cadastro.preset.nomepreset')} />

      <Autocomplete
        name="idposto"
        label="codigonomeposto"
        url="postos"
        unique="idposto"
        title={t('s.geral.postocomboio')}
        disabled={!create}
      />
      <Input
        name="enderecopreset"
        label={t('s.cadastro.preset.enderecopreset')}
      />
      <Select
        name="conexaoclientepreset"
        label={t('s.cadastro.preset.conexaoclientepreset')}
        options={tipoConexao}
      />
      <Select
        name="tipopreset"
        label={t('s.cadastro.preset.tipopreset')}
        options={optionTCD}
      />

      <Hidden name="tipocarregamentopreset" />

      <Select
        name="statuspreset"
        label={t('s.cadastro.pitstop.statusobservacao')}
        options={options}
      />
      <Input
        name="ordempreset"
        label={t('s.cadastro.preset.ordempreset')}
        type="number"
      />
      <Input
        name="portatcppreset"
        label={t('s.cadastro.preset.portatcppreset')}
        type="number"
      />

      <Accordion expanded>
        <AccordionSummary id="panel-head">
          <Typography>{t('s.menu.configuracao.produto')}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Preset
            onCallback={(values) => handleChangeComponents(values)}
            components={components}
            arms={[]}
            viscosidades={[]}
            groups={[]} // groups
            data={
              create || isEmpty(find)
                ? components
                : find.produtos.map((reven) => ({
                    id: Math.random().toString(),
                    ...reven,
                  }))
            }
          />
        </AccordionDetails>
      </Accordion>

      <Accordion expanded>
        <AccordionSummary id="panel-head">
          <Typography>
            {t('s.cadastro.presetreceita.idpresetreceita')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Revenues
            onCallback={(values) => setRevenues(values)}
            components={components}
            data={
              create || isEmpty(find)
                ? revenues
                : find.receitas.map((reven) => ({
                    id: Math.random().toString(),
                    ...reven,
                  }))
            }
          />
        </AccordionDetails>
      </Accordion>
    </Form>
  );
}

RequestForm.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

RequestForm.defaultProps = {
  data: {},
};
